import { forwardRef, useImperativeHandle, useRef } from 'react';

import { LicenseGMetrixTableDialog, LicenseGMetrixTableDialogHandle } from 'components/LicenseGMetrixTableDialog/LicenseGMetrixTableDialog';
import { useLicenseDefaultLookupSearchFields } from 'components/GMetrixTable/gmetrixTableSearchFields';
import { LicenseGMetrixTable } from 'components/LicenseGMetrixTable/LicenseGMetrixTable';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { usePartnerCreateEdit } from 'pages/partner_create_edit/context';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { PartnerApi } from 'apis/PartnerApi';
import { License } from 'apis/LicenseApi';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

type LicensesSectionProps = {
    licenses: License[];
    onStageAdd?: (licenses: License[]) => void;
    onStageRemove?: (license: License) => void;
    partnerId: number;
    partnersCurrentLicenses: License[];
    searchAutoFocus?: boolean;
    setLicenses: (licenses: License[]) => void;
    setPartnersCurrentLicenses: (licenses: License[]) => void;
    stagedMode?: boolean;
}

export type LicensesSectionHandle = {
    openAddDialog: () => void;
}

export const LicensesSection = forwardRef<LicensesSectionHandle, LicensesSectionProps>(({ licenses, onStageAdd, onStageRemove, partnerId, partnersCurrentLicenses, searchAutoFocus, setLicenses, setPartnersCurrentLicenses, stagedMode }, ref) => {

    const [state, setState] = useSuperState({
        loadingGridData: false,
        searchTerm: ''
    });

    const { highestRole } = useApp();
    const { formatMessage } = useIntl();
    const { permissions } = usePartnerCreateEdit();
    const { catchWithToast } = useNotification();
    const licenseDefaultLookupSearchFields = useLicenseDefaultLookupSearchFields();

    const gmetrixConfirmRef = useRef<any>(null);
    const licenseDialogRef = useRef<LicenseGMetrixTableDialogHandle>(null);

    useImperativeHandle(ref, () => ({
        openAddDialog: async () => {

            const result = await licenseDialogRef.current?.show();
            if (!result) return;
            addLicenseToPartner(result.licenses);

        }
    }));

    const addLicenseToPartner = (addedLicenses: License[]) => {

        if (stagedMode) {

            onStageAdd?.(addedLicenses);
            return;

        }

        PartnerApi.AddLicensesToPartner(
            partnerId,
            addedLicenses.map(license => license.Id),
            addedLicenses.map(license => license.SuppliedBy)
        ).catch(catchWithToast(formatMessage({ id: 'failed_to_add_licenses_to_partner.c8f99849', defaultMessage: 'Failed to add licenses to partner.' })));
        setLicenses(licenses.filter(license => !addedLicenses.find(added => added.ID === license.ID)));
        setPartnersCurrentLicenses([...addedLicenses, ...partnersCurrentLicenses]);

    };

    const removeOrderableLicense = async (dataItem: License) => {

        const result = await gmetrixConfirmRef.current?.confirm(
            formatMessage({ id: 'license_removal.6ac46474', defaultMessage: 'License Removal' }) || 'License Removal',
            formatMessage({ id: 'are_you_sure_you_want_to_remove_this_lic.85e1f74b', defaultMessage: 'Are you sure you want to remove this License from this {partner}?' }, { partner: '' }) || 'Are you sure you want to remove this license?'
        );
        if (result) {

            if (stagedMode) {

                onStageRemove?.(dataItem);
                return;

            }

            await PartnerApi.DeleteLicenseFromPartner(partnerId, dataItem.Id).catch(catchWithToast(formatMessage({ id: 'failed_to_delete_license_from_partner.ba3dab19', defaultMessage: 'Failed to delete license from partner.' })));
            setPartnersCurrentLicenses(partnersCurrentLicenses.filter(l => l.Id !== dataItem.Id));
            setLicenses([...licenses, dataItem]);

        }

    };

    return (
        <>
            <GMetrixDialogConfirm ref={gmetrixConfirmRef} />

            {licenses.length > 0 && (
                <LicenseGMetrixTableDialog
                    highestRoleId={highestRole.partnerId}
                    licenses={licenses}
                    ref={licenseDialogRef}
                    setLicenses={setLicenses}
                />
            )}

            <LicenseGMetrixTable
                manualLoadGridData
                manuallyRenderLicenses
                columnData={[
                    { field: 'Id', title: formatMessage({ id: 'license_id.87d2b466', defaultMessage: 'License ID' }) || 'License ID' },
                    { field: 'Name', title: formatMessage({ id: 'name.5458f379', defaultMessage: 'Name' }) || 'Name' },
                    { field: 'SuppliedByName', title: formatMessage({ id: 'supplied_by.360c7c18', defaultMessage: 'Supplied By' }) || 'Supplied By' },
                    { field: 'LicenseTypeString', title: formatMessage({ id: 'type.b65913c1', defaultMessage: 'Type', description: 'Formulario' }) || 'Type' },
                    {
                        cell: ({ dataItem }) => (
                            <td>
                                <div className="d-flex justify-content-end">
                                    {!permissions.IsAssociation && (
                                        <GMetrixButton
                                            disabled={!stagedMode && partnersCurrentLicenses.length <= 1}
                                            onClick={() => removeOrderableLicense(dataItem)}
                                            variant="outline-danger"
                                        >
                                            {<FormattedMessage defaultMessage="Remove" id="remove.d34ac1e7" /> || 'Remove'}
                                        </GMetrixButton>
                                    )}
                                </div>
                            </td>
                        ),
                        field: 'actions' as any,
                        title: '',
                        width: 160
                    }
                ]}
                advancedSearchFields={licenseDefaultLookupSearchFields}

                className="p-0"
                defaultItemsPerPage={10}
                licenses={partnersCurrentLicenses}
                loadingGridData={state.loadingGridData}
                searchAutoFocus={searchAutoFocus}
                searchTerm={state.searchTerm}
                selectedLicenses={[]}
                setLicenses={setPartnersCurrentLicenses}
                setLoadingGridData={val => setState({ loadingGridData: typeof val === 'function' ? val(state.loadingGridData) : val })}
                setSearchTerm={val => setState({ searchTerm: val })}
            />
        </>
    );

});

LicensesSection.displayName = 'LicensesSection';
